export type McpEmbedToken = {
  accessToken: string
  expiresAt: string
}

type ChannelMessage =
  | {
      type: 'mcp-embed-token:request'
      userId: string
      requestId: string
    }
  | {
      type: 'mcp-embed-token:response'
      userId: string
      requestId: string
      token: McpEmbedToken
    }
  | {
      type: 'mcp-embed-token:available'
      userId: string
      token: McpEmbedToken
    }

type BroadcastChannelLike = {
  postMessage: (message: ChannelMessage) => void
  addEventListener: (type: 'message', listener: (event: MessageEvent<ChannelMessage>) => void) => void
  removeEventListener: (
    type: 'message',
    listener: (event: MessageEvent<ChannelMessage>) => void,
  ) => void
  close: () => void
}

type PendingRequest = {
  resolve: (token: McpEmbedToken | null) => void
  timeoutId: ReturnType<typeof setTimeout>
}

type McpEmbedTokenBrokerOptions = {
  userId: string
  createChannel?: (name: string) => BroadcastChannelLike
  now?: () => number
  requestTimeoutMs?: number
  createRequestId?: () => string
}

export type McpEmbedTokenBroker = {
  publishToken: (token: McpEmbedToken) => boolean
  requestToken: () => Promise<McpEmbedToken | null>
  close: () => void
}

const CHANNEL_PREFIX = 'axioma:mcp2-embed-token:v1'
const MIN_TOKEN_VALIDITY_MS = 60 * 1000
const DEFAULT_REQUEST_TIMEOUT_MS = 500

export const isUsableMcpEmbedToken = (
  token: McpEmbedToken | null | undefined,
  now = Date.now(),
): token is McpEmbedToken => {
  if (!token?.accessToken || !token.expiresAt) return false
  const expiresAt = Date.parse(token.expiresAt)
  return Number.isFinite(expiresAt) && expiresAt - now > MIN_TOKEN_VALIDITY_MS
}

export const createMcpEmbedTokenBroker = ({
  userId,
  createChannel,
  now = () => Date.now(),
  requestTimeoutMs = DEFAULT_REQUEST_TIMEOUT_MS,
  createRequestId = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`,
}: McpEmbedTokenBrokerOptions): McpEmbedTokenBroker | null => {
  const normalizedUserId = String(userId || '').trim()
  const channelFactory =
    createChannel ||
    (typeof BroadcastChannel === 'function'
      ? (name: string) => new BroadcastChannel(name)
      : null)

  if (!normalizedUserId || !channelFactory) return null

  const channel = channelFactory(`${CHANNEL_PREFIX}:${encodeURIComponent(normalizedUserId)}`)
  const pendingRequests = new Map<string, PendingRequest>()
  let currentToken: McpEmbedToken | null = null
  let closed = false

  const rememberToken = (token: McpEmbedToken | null | undefined) => {
    if (!isUsableMcpEmbedToken(token, now())) return false
    currentToken = token
    return true
  }

  const handleMessage = (event: MessageEvent<ChannelMessage>) => {
    const message = event?.data
    if (!message || message.userId !== normalizedUserId) return

    if (message.type === 'mcp-embed-token:request') {
      if (!isUsableMcpEmbedToken(currentToken, now())) return
      channel.postMessage({
        type: 'mcp-embed-token:response',
        userId: normalizedUserId,
        requestId: message.requestId,
        token: currentToken,
      })
      return
    }

    if (!rememberToken(message.token)) return
    if (message.type !== 'mcp-embed-token:response') return

    const pendingRequest = pendingRequests.get(message.requestId)
    if (!pendingRequest) return
    clearTimeout(pendingRequest.timeoutId)
    pendingRequests.delete(message.requestId)
    pendingRequest.resolve(currentToken)
  }

  channel.addEventListener('message', handleMessage)

  return {
    publishToken: token => {
      if (closed || !rememberToken(token)) return false
      channel.postMessage({
        type: 'mcp-embed-token:available',
        userId: normalizedUserId,
        token,
      })
      return true
    },
    requestToken: () => {
      if (closed) return Promise.resolve(null)
      if (isUsableMcpEmbedToken(currentToken, now())) {
        return Promise.resolve(currentToken)
      }

      const requestId = createRequestId()
      return new Promise(resolve => {
        const timeoutId = setTimeout(() => {
          pendingRequests.delete(requestId)
          resolve(null)
        }, requestTimeoutMs)

        pendingRequests.set(requestId, { resolve, timeoutId })
        channel.postMessage({
          type: 'mcp-embed-token:request',
          userId: normalizedUserId,
          requestId,
        })
      })
    },
    close: () => {
      if (closed) return
      closed = true
      channel.removeEventListener('message', handleMessage)
      channel.close()
      pendingRequests.forEach(pendingRequest => {
        clearTimeout(pendingRequest.timeoutId)
        pendingRequest.resolve(null)
      })
      pendingRequests.clear()
      currentToken = null
    },
  }
}
